// Feb 2026 · Floating Project Navigator.
//
// A fixed-position pill (First · Prev · N/M · Next · Last) that lets a
// PM cycle through every project they have access to without going
// back to the Projects list.  Opens from the Project header "Sort" chip
// right next to the quick switcher.
//
// The navigator is:
//   • draggable (header grip handle, pointer events, 60fps via rAF)
//   • position-persisted per workspace in localStorage
//   • sort-aware — the current sort is picked from `useProjectSort`
//     so the Projects list and the Project detail header share the
//     same ordering.  Changing the sort inside the navigator reorders
//     the sibling list on both screens.
//   • tab-preserving — navigation keeps the current `?tab=` so a PM
//     doing a Billing review doesn't get kicked back to Dashboard on
//     every project switch.
//   • viewport-clamped on resize so a saved position never lands
//     off-screen.
//
// No new deps.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import {
  SkipBack, SkipForward, ChevronLeft, ChevronRight, X,
  ArrowUpDown, ArrowDown, ArrowUp, GripVertical,
} from "lucide-react";

/**
 * Shared project-sort state across the Projects list and the Project
 * detail navigator.  Persists per-workspace so a PM's chosen review
 * order survives reloads.
 *
 * sortKey ∈  "name" | "priority" | "pm" | "total_tasks" | "open_tasks"
 *          | "delayed_tasks" | "overbudget_tasks" | "billable_tasks"
 *          | "billed_tasks"  | "unpaid_tasks"
 * sortDir ∈ "asc" | "desc"
 */
export const SORT_OPTIONS = [
  { key: "name",             label: "Alphabetical",   accessor: (p) => (p.name || "").toLowerCase() },
  { key: "priority",         label: "Priority",       accessor: (p) => _priorityRank(p.priority) },
  // Feb 2026 · More ways to triage: by health (red → green), status
  // (ordered by lifecycle rather than alphabetical), and age
  // (oldest-first on asc so a PM can review long-running projects
  // in the order they were created).
  { key: "health",           label: "Health",         accessor: (p) => _healthRank(p.health) },
  { key: "status",           label: "Status",         accessor: (p) => _statusRank(p.status) },
  { key: "project_age",      label: "Project age",    accessor: (p) => _ageMs(p.created_at) },
  { key: "pm",               label: "Project manager",accessor: (p) => (p.pm_name || p.project_manager_name || p.owner_name || p.created_by_name || "").toLowerCase() },
  { key: "total_tasks",      label: "Total tasks",    accessor: (p) => Number(p.task_count ?? p.total_tasks ?? p.tasks_total ?? 0) },
  { key: "open_tasks",       label: "Open tasks",     accessor: (p) => Number(p.open_tasks ?? p.open_tasks_count ?? 0) },
  { key: "delayed_tasks",    label: "Delayed tasks",  accessor: (p) => Number(p.delayed_tasks ?? p.overdue_tasks ?? p.late_tasks ?? 0) },
  { key: "delayed_days_total", label: "Days delayed", accessor: (p) => Number(p.delayed_days_total ?? 0) },
  { key: "overbudget_tasks", label: "Over-budget",    accessor: (p) => Number(p.overbudget_tasks ?? p.over_budget_tasks ?? 0) },
  { key: "billable_tasks",   label: "Billable tasks", accessor: (p) => Number(p.billable_tasks ?? p.billable_count ?? 0) },
  { key: "billed_tasks",     label: "Billed tasks",   accessor: (p) => Number(p.billed_tasks ?? p.billed_count ?? 0) },
  { key: "unpaid_tasks",     label: "Unpaid tasks",   accessor: (p) => Number(p.unpaid_tasks ?? p.unpaid_count ?? 0) },
];

function _priorityRank(p) {
  // Highest priority first when sorting ascending feels unnatural, so
  // we invert the rank: urgent gets the LARGEST number. Combined with
  // the user's direction toggle this gives sensible UX either way.
  const m = { urgent: 4, high: 3, medium: 2, low: 1 };
  return m[(p || "").toLowerCase()] ?? 0;
}

function _healthRank(h) {
  // At-risk / off-track first so sorting DESC puts red at the top —
  // what a PM reviewing their book of work actually wants to see.
  const m = { "red": 3, "off_track": 3, "at_risk": 2, "amber": 2, "yellow": 2,
              "green": 1, "on_track": 1, "unknown": 0, "": 0 };
  return m[(h || "").toLowerCase()] ?? 0;
}

function _statusRank(s) {
  // Lifecycle order so sorting ASC reads planning → active → …
  // Keeps "closed-ish" statuses at one end regardless of alpha.
  const m = { "planning": 1, "not_started": 2, "active": 3, "in_progress": 4,
              "on_hold": 5, "paused": 5, "review": 6, "qa": 6,
              "completed": 7, "done": 7, "closed": 8, "archived": 9,
              "cancelled": 10 };
  return m[(s || "").toLowerCase()] ?? 0;
}

function _ageMs(created_at) {
  if (!created_at) return 0;
  const t = new Date(created_at).getTime();
  if (isNaN(t)) return 0;
  // Age = time since creation. Sorting ASC = youngest first (small
  // age), DESC = oldest first (large age). PMs usually want the
  // long-running projects at the top so DESC is the default we
  // encourage via a sensible default tip.
  return Date.now() - t;
}

export function useProjectSort(wsId) {
  const lsKey = `projects.sort.${wsId || "_"}`;
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(lsKey);
      if (!raw) return { key: "name", dir: "asc" };
      const parsed = JSON.parse(raw);
      if (!parsed?.key) return { key: "name", dir: "asc" };
      return { key: parsed.key, dir: parsed.dir === "desc" ? "desc" : "asc" };
    } catch { return { key: "name", dir: "asc" }; }
  });
  useEffect(() => {
    try { localStorage.setItem(lsKey, JSON.stringify(state)); } catch { /* quota */ }
  }, [lsKey, state]);
  const setKey = useCallback((k) => setState((s) => ({ ...s, key: k })), []);
  const setDir = useCallback((d) => setState((s) => ({ ...s, dir: d })), []);
  const toggleDir = useCallback(() => setState((s) => ({ ...s, dir: s.dir === "asc" ? "desc" : "asc" })), []);
  return { sortKey: state.key, sortDir: state.dir, setKey, setDir, toggleDir };
}

export function sortProjects(projects, sortKey, sortDir) {
  const opt = SORT_OPTIONS.find((o) => o.key === sortKey) || SORT_OPTIONS[0];
  const acc = opt.accessor;
  const mult = sortDir === "desc" ? -1 : 1;
  const copy = [...(projects || [])];
  copy.sort((a, b) => {
    const av = acc(a); const bv = acc(b);
    if (av === bv) {
      // Stable tie-breaker on name so output is deterministic.
      const an = (a.name || "").toLowerCase();
      const bn = (b.name || "").toLowerCase();
      return an < bn ? -1 : an > bn ? 1 : 0;
    }
    if (typeof av === "number" && typeof bv === "number") return (av - bv) * mult;
    return (String(av) < String(bv) ? -1 : 1) * mult;
  });
  return copy;
}

/**
 * Small chip rendered next to the Project quick switcher so a PM can
 * reorder the siblings without opening the floating navigator.  Shows
 * the current sort name + direction glyph.
 */
export function ProjectSortChip({ sortKey, sortDir, onChangeKey, onToggleDir, testid = "project-sort-chip" }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (!btnRef.current) return;
      if (!btnRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);
  const current = SORT_OPTIONS.find((o) => o.key === sortKey) || SORT_OPTIONS[0];
  return (
    <div className="relative inline-flex items-stretch" ref={btnRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 border border-border bg-background hover:bg-muted px-2 py-1 text-[11px] rounded-none"
        title="Choose how sibling projects are ordered — used by the navigator and the Projects list"
        data-testid={testid}
      >
        <ArrowUpDown className="w-3 h-3 text-muted-foreground" />
        <span className="font-mono">{current.label}</span>
      </button>
      <button
        type="button"
        onClick={onToggleDir}
        className="inline-flex items-center border border-l-0 border-border bg-background hover:bg-muted px-1.5 py-1 text-[11px] rounded-none"
        title={sortDir === "asc" ? "Ascending — tap to switch to descending" : "Descending — tap to switch to ascending"}
        data-testid={`${testid}-dir`}
      >
        {sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
      </button>
      {open && (
        <div
          className="absolute top-full left-0 mt-1 bg-background border border-border shadow-lg z-50 py-1 min-w-[180px]"
          role="listbox"
          data-testid={`${testid}-menu`}
        >
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              role="option"
              aria-selected={opt.key === sortKey}
              onClick={() => { onChangeKey(opt.key); setOpen(false); }}
              className={`w-full text-left px-2 py-1 text-[11px] hover:bg-muted ${opt.key === sortKey ? "bg-primary/10 text-primary font-semibold" : ""}`}
              data-testid={`${testid}-opt-${opt.key}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Floating pill navigator rendered via portal.  Draggable; persists
 * its position per workspace.  Switches projects by navigating to the
 * sibling id, preserving the current `?tab=` query so sub-tabs stick.
 */
export default function FloatingProjectNavigator({ wsId, projects, currentId, sortKey, sortDir, onChangeKey, onToggleDir, onDismiss }) {
  const nav = useNavigate();
  const loc = useLocation();
  const sorted = useMemo(
    () => sortProjects(projects, sortKey, sortDir),
    [projects, sortKey, sortDir]
  );
  const total = sorted.length;
  const currentIdx = useMemo(
    () => sorted.findIndex((p) => p.id === currentId),
    [sorted, currentId]
  );
  const atFirst = currentIdx <= 0;
  const atLast  = currentIdx === -1 || currentIdx === total - 1;

  // Feb 2026 · Pause-on-Open — hide the pill whenever any modal /
  // dialog is open so the floating controls never sit on top of a
  // dialog's close button. Listens for `role="dialog"` nodes being
  // added/removed from the DOM via MutationObserver (works for every
  // Dialog in the app — Edit task, Create meeting, Rate, Reschedule).
  const [modalOpen, setModalOpen] = useState(false);
  useEffect(() => {
    const detect = () => {
      // Radix Dialog adds `[role="dialog"][data-state="open"]`; our
      // custom dialogs render `role="dialog"` without the data-state.
      const anyOpen = !!document.querySelector(
        '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]'
      );
      setModalOpen(anyOpen);
    };
    detect();
    const mo = new MutationObserver(detect);
    mo.observe(document.body, { subtree: true, attributes: true, childList: true, attributeFilter: ["data-state", "role"] });
    return () => mo.disconnect();
  }, []);

  // Feb 2026 · Resume — remember the last project the user cycled to
  // on this device so they can continue a review session later. Keyed
  // on workspace + sort so switching sort modes doesn't drag the
  // bookmark into an unrelated list.
  const resumeKey = `projects.navigator.resume.${wsId || "_"}.${sortKey}.${sortDir}`;
  useEffect(() => {
    if (!currentId) return;
    try { localStorage.setItem(resumeKey, currentId); } catch { /* quota */ }
  }, [currentId, resumeKey]);
  const resumeId = useMemo(() => {
    try { return localStorage.getItem(resumeKey) || null; }
    catch { return null; }
  }, [resumeKey]);
  const resumeIdx = useMemo(
    () => (resumeId ? sorted.findIndex((p) => p.id === resumeId) : -1),
    [sorted, resumeId]
  );
  const hasResume = resumeId && resumeId !== currentId && resumeIdx >= 0;

  // Feb 2026 · Session breadcrumbs — the last 5 projects the user
  // cycled through this browser session.  Kept in sessionStorage
  // (clears on tab close) so a long review session shows a jump-back
  // trail without polluting localStorage. Deduped so revisits don't
  // push older entries out of the window prematurely.
  const trailKey = `projects.navigator.trail.${wsId || "_"}`;
  const [trail, setTrail] = useState(() => {
    try {
      const raw = sessionStorage.getItem(trailKey);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  useEffect(() => {
    if (!currentId || !sorted.length) return;
    const cur = sorted.find((p) => p.id === currentId);
    if (!cur) return;
    setTrail((prev) => {
      // Push current (as the "where I am now") only if it differs
      // from the last pushed entry; keep a window of 5.
      const filtered = prev.filter((e) => e.id !== currentId);
      const next = [{ id: currentId, name: cur.name || "Untitled", at: Date.now() }, ...filtered].slice(0, 5);
      try { sessionStorage.setItem(trailKey, JSON.stringify(next)); } catch { /* quota */ }
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId, sorted.length]);
  const trailOthers = useMemo(
    () => trail.filter((e) => e.id !== currentId).slice(0, 4),
    [trail, currentId]
  );

  const goTo = useCallback((idx) => {
    if (idx < 0 || idx >= total) return;
    const target = sorted[idx];
    if (!target || target.id === currentId) return;
    // Preserve `?tab=` + any other query params so sub-tab survives.
    const search = loc.search || "";
    nav(`/app/projects/${target.id}${search}`);
  }, [sorted, currentId, total, loc.search, nav]);

  // Draggable position
  const dragRef = useRef(null);
  const rafRef = useRef(0);
  const lsKey = `projects.navigator.pos.${wsId || "_"}`;
  const [pos, setPos] = useState(() => {
    const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
    const vh = typeof window !== "undefined" ? window.innerHeight : 768;
    const defaults = { x: Math.max(16, vw / 2 - 220), y: Math.max(72, vh - 72) };
    try {
      const raw = localStorage.getItem(lsKey);
      if (!raw) return defaults;
      const parsed = JSON.parse(raw);
      return {
        x: Math.max(8, Math.min(parsed.x ?? defaults.x, vw - 440 - 8)),
        y: Math.max(56, Math.min(parsed.y ?? defaults.y, vh - 60)),
      };
    } catch { return defaults; }
  });
  useEffect(() => {
    const id = window.setTimeout(() => {
      try { localStorage.setItem(lsKey, JSON.stringify(pos)); } catch { /* quota */ }
    }, 200);
    return () => window.clearTimeout(id);
  }, [pos, lsKey]);
  useEffect(() => {
    const onResize = () => {
      setPos((p) => {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        return {
          x: Math.max(8, Math.min(p.x, vw - 440 - 8)),
          y: Math.max(56, Math.min(p.y, vh - 60)),
        };
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const beginDrag = useCallback((e) => {
    if (e.button != null && e.button !== 0) return;
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y };
    const onMove = (ev) => {
      if (!dragRef.current) return;
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const d = dragRef.current;
        if (!d) return;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const x = Math.max(8, Math.min(d.origX + (ev.clientX - d.startX), vw - 440 - 8));
        const y = Math.max(56, Math.min(d.origY + (ev.clientY - d.startY), vh - 60));
        setPos({ x, y });
      });
    };
    const onUp = () => {
      dragRef.current = null;
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
    document.body.style.userSelect = "none";
    document.body.style.cursor = "grabbing";
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }, [pos.x, pos.y]);

  // Keyboard shortcuts: ← prev, → next, Home first, End last. Scoped
  // to when no input is focused so typing in a comment doesn't jump
  // the user off the project.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) || "";
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(tag) || e.target?.isContentEditable) return;
      if (e.altKey || e.metaKey || e.ctrlKey) return;
      if (e.key === "ArrowLeft")  { e.preventDefault(); goTo(currentIdx - 1); }
      else if (e.key === "ArrowRight") { e.preventDefault(); goTo(currentIdx + 1); }
      else if (e.key === "Home")  { e.preventDefault(); goTo(0); }
      else if (e.key === "End")   { e.preventDefault(); goTo(total - 1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [currentIdx, total, goTo]);

  if (!total) return null;
  if (modalOpen) return null;

  const btnBase = "h-7 w-7 flex items-center justify-center text-white/90 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors";

  return createPortal(
    <div
      className="fixed z-[70] flex flex-col items-stretch gap-1"
      style={{ left: pos.x, top: pos.y }}
      data-testid="project-floating-nav-wrap"
    >
    <div
      className="bg-neutral-900 text-white shadow-2xl ring-1 ring-white/10 rounded-full pl-1 pr-1 py-0.5 flex items-center"
      role="toolbar"
      aria-label="Project navigator"
      data-testid="project-floating-nav"
    >
      {/* Drag handle */}
      <button
        type="button"
        onPointerDown={beginDrag}
        className="h-7 w-5 flex items-center justify-center text-white/50 hover:text-white/90 cursor-grab active:cursor-grabbing"
        title="Drag to move"
        data-testid="project-floating-nav-drag"
      >
        <GripVertical className="w-3 h-3" />
      </button>
      <span className="inline-flex items-center gap-1 pr-1 text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
        <span className="hidden sm:inline">Project</span>
      </span>
      <button type="button" onClick={() => goTo(0)} disabled={atFirst} className={btnBase}
              title="Jump to first project (Home)" data-testid="project-floating-nav-first">
        <SkipBack className="w-3.5 h-3.5" />
      </button>
      <button type="button" onClick={() => goTo(currentIdx - 1)} disabled={atFirst} className={btnBase}
              title="Previous project (←)" data-testid="project-floating-nav-prev">
        <ChevronLeft className="w-3.5 h-3.5" />
      </button>
      <span
        className="px-1.5 h-7 flex items-center text-[11px] tabular-nums font-mono text-white/90 min-w-[52px] justify-center"
        title={total ? _counterTooltip({ sorted, currentIdx, total, sortKey, sortDir }) : ""}
        data-testid="project-floating-nav-counter"
      >
        {currentIdx === -1 ? "–" : currentIdx + 1} <span className="opacity-40 mx-1">/</span> {total}
      </span>
      {/* Feb 2026 · Context chip — surfaces the sort-dependent magnitude
          of the current project so the PM knows "what am I about to
          see" before clicking. For days-delayed it reads e.g.
          "63d · 7 tasks"; for billing sorts it reads the raw count;
          for alphabetical / PM / status we keep it hidden to avoid
          noise. */}
      {_contextChip({ project: sorted[currentIdx], sortKey }) && (
        <span
          className="h-5 px-1.5 text-[10px] font-mono tabular-nums text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-sm ml-1"
          title={_contextChipTitle({ project: sorted[currentIdx], sortKey })}
          data-testid="project-floating-nav-context"
        >
          {_contextChip({ project: sorted[currentIdx], sortKey })}
        </span>
      )}
      <button type="button" onClick={() => goTo(currentIdx + 1)} disabled={atLast} className={btnBase}
              title="Next project (→)" data-testid="project-floating-nav-next">
        <ChevronRight className="w-3.5 h-3.5" />
      </button>
      <button type="button" onClick={() => goTo(total - 1)} disabled={atLast} className={btnBase}
              title="Jump to last project (End)" data-testid="project-floating-nav-last">
        <SkipForward className="w-3.5 h-3.5" />
      </button>
      {hasResume && (
        <button
          type="button"
          onClick={() => goTo(resumeIdx)}
          className="h-7 px-1.5 text-[10px] uppercase tracking-widest text-amber-300 hover:bg-white/10 inline-flex items-center gap-1"
          title={`Resume where you left off (position ${resumeIdx + 1})`}
          data-testid="project-floating-nav-resume"
        >
          ↩ <span className="hidden md:inline">Resume</span>
        </button>
      )}
      {/* Sort selector — inline inside the pill so a PM can reorder
          without leaving the project. */}
      <span className="mx-1 w-px h-5 bg-white/15" aria-hidden="true" />
      <NavSortDropdown
        sortKey={sortKey}
        sortDir={sortDir}
        onChangeKey={onChangeKey}
        onToggleDir={onToggleDir}
      />
      <button
        type="button"
        onClick={onDismiss}
        className="h-7 w-7 flex items-center justify-center text-white/60 hover:bg-white/10 hover:text-white transition-colors ml-0.5"
        title="Hide the navigator"
        data-testid="project-floating-nav-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
    {/* Session breadcrumbs — last 4 projects cycled in this tab. Lets
        a PM backtrack to any of the previously-visited ones without
        hunting through the list. Hidden entirely when there's nothing
        meaningful to show (first project in session). */}
    {trailOthers.length > 0 && (
      <div
        className="self-start bg-neutral-900/95 text-white shadow-xl ring-1 ring-white/10 rounded-full py-0.5 pl-2 pr-1 flex items-center gap-1 max-w-[500px] overflow-hidden"
        role="group"
        aria-label="Recently cycled projects"
        data-testid="project-floating-nav-trail"
      >
        <span className="text-[9px] uppercase tracking-widest text-white/40 shrink-0">Trail</span>
        {trailOthers.map((entry) => (
          <button
            key={entry.id}
            type="button"
            onClick={() => {
              const idx = sorted.findIndex((p) => p.id === entry.id);
              if (idx >= 0) goTo(idx);
              else {
                // Project is no longer in the sorted list (filtered
                // out). Navigate directly, preserving the tab.
                nav(`/app/projects/${entry.id}${loc.search || ""}`);
              }
            }}
            title={`Back to "${entry.name}"`}
            className="h-6 px-1.5 text-[10px] max-w-[120px] truncate border border-white/10 bg-white/5 hover:bg-white/15 text-white/80 hover:text-white rounded-full"
            data-testid={`project-floating-nav-trail-${entry.id}`}
          >
            {entry.name}
          </button>
        ))}
        <button
          type="button"
          onClick={() => {
            try { sessionStorage.removeItem(trailKey); } catch { /* noop */ }
            setTrail([]);
          }}
          className="h-6 w-6 flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 rounded-full"
          title="Clear trail"
          data-testid="project-floating-nav-trail-clear"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    )}
    </div>,
    document.body
  );
}

function NavSortDropdown({ sortKey, sortDir, onChangeKey, onToggleDir }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const current = SORT_OPTIONS.find((o) => o.key === sortKey) || SORT_OPTIONS[0];
  return (
    <div className="relative inline-flex items-center" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 h-7 px-1.5 text-[10px] uppercase tracking-widest text-white/80 hover:bg-white/10"
        title="Sort order used by First / Prev / Next / Last"
        data-testid="project-floating-nav-sort"
      >
        <ArrowUpDown className="w-3 h-3" /> <span className="hidden md:inline normal-case tracking-normal font-mono">{current.label}</span>
      </button>
      <button
        type="button"
        onClick={onToggleDir}
        className="h-7 w-5 flex items-center justify-center text-white/70 hover:bg-white/10"
        title={sortDir === "asc" ? "Ascending — tap to flip" : "Descending — tap to flip"}
        data-testid="project-floating-nav-sort-dir"
      >
        {sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
      </button>
      {open && (
        <div
          className="absolute bottom-full right-0 mb-1 bg-neutral-900 text-white border border-white/10 shadow-2xl py-1 min-w-[180px] rounded-none"
          role="listbox"
          data-testid="project-floating-nav-sort-menu"
        >
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              role="option"
              aria-selected={opt.key === sortKey}
              onClick={() => { onChangeKey(opt.key); setOpen(false); }}
              className={`w-full text-left px-2 py-1 text-[11px] hover:bg-white/10 ${opt.key === sortKey ? "bg-white/10 text-emerald-300 font-semibold" : ""}`}
              data-testid={`project-floating-nav-sort-opt-${opt.key}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


// Feb 2026 · Floating-nav context helpers.
//
// Sort-aware chip + tooltip that surfaces the magnitude behind the
// current project's position in the sorted list.  Hidden for sort
// modes where the project's rank doesn't convey a useful number
// (name / PM / status / health / age).
function _contextChip({ project, sortKey }) {
  if (!project) return null;
  switch (sortKey) {
    case "delayed_days_total": {
      const d = Number(project.delayed_days_total || 0);
      const n = Number(project.delayed_tasks || 0);
      if (!d && !n) return null;
      return `${d}d · ${n}`;
    }
    case "delayed_tasks":    return project.delayed_tasks    ? `${project.delayed_tasks} late`      : null;
    case "overbudget_tasks": return project.overbudget_tasks ? `${project.overbudget_tasks} over`   : null;
    case "unpaid_tasks":     return project.unpaid_tasks     ? `${project.unpaid_tasks} unpaid`     : null;
    case "billed_tasks":     return project.billed_tasks     ? `${project.billed_tasks} billed`     : null;
    case "billable_tasks":   return project.billable_tasks   ? `${project.billable_tasks} billable` : null;
    case "open_tasks":       return project.open_tasks       ? `${project.open_tasks} open`         : null;
    case "total_tasks":      return (project.total_tasks ?? project.task_count) ? `${project.total_tasks ?? project.task_count}` : null;
    default:                 return null;
  }
}

function _contextChipTitle({ project, sortKey }) {
  if (!project) return "";
  if (sortKey === "delayed_days_total") {
    const d = Number(project.delayed_days_total || 0);
    const n = Number(project.delayed_tasks || 0);
    const avg = n > 0 ? Math.round((d / n) * 10) / 10 : 0;
    return `${d} day-slip across ${n} task${n === 1 ? "" : "s"} · avg ${avg} day${avg === 1 ? "" : "s"} each`;
  }
  const label = (SORT_OPTIONS.find((o) => o.key === sortKey) || {}).label || sortKey;
  return `Sorted by ${label}`;
}

function _counterTooltip({ sorted, currentIdx, total, sortKey, sortDir }) {
  const label = (SORT_OPTIONS.find((o) => o.key === sortKey) || SORT_OPTIONS[0]).label;
  const base = `On ${(currentIdx === -1 ? "–" : currentIdx + 1)} of ${total} · sorted by ${label} (${sortDir})`;
  const cur = sorted?.[currentIdx];
  if (!cur) return base;
  if (sortKey === "delayed_days_total" && Number(cur.delayed_days_total || 0) > 0) {
    const d = Number(cur.delayed_days_total || 0);
    const n = Number(cur.delayed_tasks || 0);
    return `${base}\n${d} day-slip across ${n} task${n === 1 ? "" : "s"}`;
  }
  return base;
}
